import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription, of as observableOf } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { State } from '®app/ngrx/state';
import {
  SaveableReadingIntention as SaveableReadingIntentionAliased,
  SaveableStudyNote as SaveableStudyNoteAliased,
} from '®shared/annotations';
import {
  BibliographicResource,
  OnlineResource,
} from '®shared/bibliographic-resource';
import {
  CURRENT_USER,
  CurrentUserAndPermissions,
  CurrentUserService,
} from '®shared/current-user';
import { DashboardAnalyticsService } from '®shared/dashboard-analytics';
import { LoadableFactory } from '®shared/data';
import { EventsService } from '®shared/events';
import {
  LAUNCH_METHOD,
  LaunchMethod,
} from '®shared/events/launch-method.constant';
import { LAUNCH_MODE, LaunchMode } from '®shared/events/launch-mode.constant';
import {
  FetchPageContentAction,
  LoadablePageResource,
  selectPageContentForId,
} from '®shared/list-item';
import {
  ListItemClickAction,
  ListItemClickActionTypeAndSubtype,
  ListItemEvents,
  OnlineResourceClickEvent,
  createListItemClickEvent,
  listItemClickAction,
  listItemClickActionForOnlineResourceClick,
  listItemClickActionForReadingIntention,
  listItemClickActionForToggle,
} from '®shared/list-item/events';
import { ReportBrokenLinkShowModalAction } from '®shared/list-item/report-broken-link-modal/report-broken-link.actions';
import { ShowShareSheetModalAction } from '®shared/list-item/share-sheet';
import {
  CollapseItemAction,
  ExpandItemAction,
  selectCurrentCitationForItem,
  selectExpandedItemId,
  selectHasCurrentCitationStyle,
  selectListItemsById,
  selectOnlineResourcesByItemId,
} from '®shared/list-item/store';
import { Citation, EditableItem } from '®shared/reading-list/models';
import { LoadableSaveableBoolean } from '®shared/user';
import {
  FetchResourceSubscriptionAction,
  UpdateResourceSubscriptionAction,
} from '®shared/user/subscribed-resources/user-subscribed-resources.actions';
import { selectResourceSubscriptionStatus } from '®shared/user/subscribed-resources/user-subscribed-resources.state';
import {
  DeleteReadingIntentionAction,
  DeleteStudyNoteAction,
  UpdateReadingIntentionAction,
  UpdateStudyNoteAction,
  areUserAnnotationsLoadedForList,
  selectReadingIntentionForUserAndResource,
  selectStudyNoteForUserAndResource,
} from '®shared/user-annotations';

import { LIST_ID } from '../common';
import {
  ListAndItem,
  ResourceIdAndUser,
  ResourceNoteAndUser,
  ResourceStatusAndUser,
  ToggleExpanded,
} from '../common/events';

type SaveableReadingIntention = SaveableReadingIntentionAliased;
type SaveableStudyNote = SaveableStudyNoteAliased;

interface ItemAndCurrentUser {
  currentUser: CurrentUserAndPermissions;
  item: EditableItem | null;
}

interface ResourceAndCurrentUser {
  currentUser: CurrentUserAndPermissions;
  resource: BibliographicResource | null;
}

@Component({
  selector: 'rl-view-item-smart',
  templateUrl: './view-item.smart-component.html',
})
export class ViewItemSmartComponent implements OnInit, OnDestroy {
  @Input() itemId: string;
  @Input() depth = 0;
  @Output() changeDimension = new EventEmitter();

  areAnnotationsResolved$: Observable<boolean>;
  showCitation$: Observable<boolean>;
  item$: Observable<EditableItem>;
  isExpanded$: Observable<boolean>;
  isSubscribedToResource$: Observable<LoadableSaveableBoolean>;
  itemCitation$: Observable<Citation | null>;
  onlineResource$: Observable<OnlineResource | null>;
  pageContent$: Observable<LoadablePageResource | null>;
  readingIntention$: Observable<SaveableReadingIntention>;
  studyNote$: Observable<SaveableStudyNote>;

  private resource$: Observable<BibliographicResource | null>;
  private listSubscription: Subscription;

  constructor(
    @Inject(CURRENT_USER) public currentUser: CurrentUserAndPermissions,
    private currentUserService: CurrentUserService,
    private dashboardAnalyticsService: DashboardAnalyticsService,
    private eventsService: EventsService,
    @Inject(LAUNCH_METHOD) private launchMethod: LaunchMethod,
    @Inject(LAUNCH_MODE) private launchMode: LaunchMode,
    @Inject(LIST_ID) public listId: string,
    private store: Store<State>,
  ) {}

  ngOnInit() {
    this.item$ = this.store
      .select(selectListItemsById)
      .pipe(map(itemsById => itemsById.get(this.itemId)));
    this.isExpanded$ = this.store
      .select(selectExpandedItemId)
      .pipe(map(expandedId => expandedId === this.itemId));
    this.onlineResource$ = this.store
      .select(selectOnlineResourcesByItemId)
      .pipe(map(onlineResourcesById => onlineResourcesById.get(this.itemId)));

    this.itemCitation$ = this.store.select(
      selectCurrentCitationForItem(this.itemId),
    );
    this.showCitation$ = this.store.select(selectHasCurrentCitationStyle);

    this.areAnnotationsResolved$ = this.item$.pipe(
      map(item => ({ currentUser: this.currentUser, item })),
      switchMap(this.areAnnotationsResolved.bind(this)),
    );

    this.resource$ = this.item$.pipe(
      map(item => (item && item.resource ? item.resource : null)),
    );

    const resourceAndUser$ = this.resource$.pipe(
      map(resource => ({
        currentUser: this.currentUser,
        resource,
      })),
    );

    this.isSubscribedToResource$ = resourceAndUser$.pipe(
      switchMap(this.subscribedToResource.bind(this)),
    );

    this.readingIntention$ = resourceAndUser$.pipe(
      switchMap(this.readingIntention.bind(this)),
    );

    this.studyNote$ = resourceAndUser$.pipe(
      switchMap(this.studyNote.bind(this)),
    );
    this.pageContent$ = this.resource$.pipe(
      switchMap(this.pageContent.bind(this)),
    );
  }

  ngOnDestroy() {
    if (this.listSubscription) {
      this.listSubscription.unsubscribe();
    }
  }

  onListItemView({ listId, itemId }: ListAndItem) {
    this.dashboardAnalyticsService.logListItemView(listId, itemId);
  }

  onReadingIntentionUpdate({ user, resource, status }: ResourceStatusAndUser) {
    if (user.isAnonymous) {
      return this.onForceLogin();
    }
    this.store.dispatch(
      new UpdateReadingIntentionAction({
        userId: user.guid,
        resourceId: resource,
        status,
      }),
    );
    this.onListItemClick(listItemClickActionForReadingIntention(status));
  }

  onReadingIntentionDelete({ user, resource }: ResourceIdAndUser) {
    if (user.isAnonymous) {
      return this.onForceLogin();
    }
    this.store.dispatch(
      new DeleteReadingIntentionAction({
        userId: user.guid,
        resourceId: resource,
      }),
    );
    this.onListItemClick(listItemClickActionForReadingIntention(null));
  }

  onStudyNoteUpdate({ user, resource, note }: ResourceNoteAndUser) {
    if (user.isAnonymous) {
      return this.onForceLogin();
    }
    this.store.dispatch(
      new UpdateStudyNoteAction({
        userId: user.guid,
        resourceId: resource,
        note,
      }),
    );
    this.onListItemClick(
      listItemClickAction(ListItemClickAction.PERSONAL_NOTE, 'update'),
    );
  }

  onStudyNoteDelete({ user, resource }: ResourceIdAndUser) {
    if (user.isAnonymous) {
      return this.onForceLogin();
    }
    this.store.dispatch(
      new DeleteStudyNoteAction({ userId: user.guid, resourceId: resource }),
    );
    this.onListItemClick(
      listItemClickAction(ListItemClickAction.PERSONAL_NOTE, 'delete'),
    );
  }

  onCheckResourceSubscription({ user, resource }: ResourceIdAndUser) {
    if (user.isAnonymous) {
      return;
    }
    this.store.dispatch(
      new FetchResourceSubscriptionAction({
        userId: user.guid,
        resourceId: resource,
      }),
    );
  }

  onShowShareSheet($event: ShowShareSheetModalAction) {
    this.store.dispatch($event);
    this.onListItemClickAction(ListItemClickAction.SHOW_SHARE_ITEM);
  }

  onSubscribeToResource({ user, resource }: ResourceIdAndUser) {
    if (user.isAnonymous) {
      return this.onForceLogin();
    }
    this.store.dispatch(
      new UpdateResourceSubscriptionAction({
        userId: user.guid,
        resourceId: resource,
      }),
    );
    this.onListItemClickAction(ListItemClickAction.ADD_TO_BOOKMARKS);
  }

  onToggleExpanded({ isExpanded, itemId }: ToggleExpanded) {
    if (isExpanded) {
      this.store.dispatch(new ExpandItemAction({ itemId }));
    } else {
      this.store.dispatch(new CollapseItemAction());
    }
    this.onListItemClick(
      listItemClickActionForToggle(isExpanded ? 'expand' : 'collapse'),
    );

    this.resource$.forEach(resource => {
      if (resource && resource.isPageResource()) {
        this.store
          .select(selectPageContentForId(resource.id))
          .pipe(
            filter(loadablePageContent => (loadablePageContent ? false : true)),
          )
          .forEach(() => {
            this.store.dispatch(
              new FetchPageContentAction({ pageId: resource.id }),
            );
          });
      }
    });
  }

  onForceLogin(): void {
    return this.currentUserService.login();
  }

  onDimensionChange($event) {
    this.changeDimension.emit($event);
  }

  onOnlineResourceClick(event: OnlineResourceClickEvent) {
    this.eventsService.recordEvent({
      className: ListItemEvents.EXTERNAL_LINK_CLICK,
      props: event,
    });
    this.onListItemClick(listItemClickActionForOnlineResourceClick(event));
  }

  private onListItemClick(action: ListItemClickActionTypeAndSubtype) {
    this.eventsService.recordEvent(
      createListItemClickEvent(
        this.listId,
        this.itemId,
        action,
        this.launchMethod,
        this.launchMode,
      ),
    );
  }

  onListItemClickAction(action: ListItemClickAction) {
    this.onListItemClick(listItemClickAction(action));
  }

  private areAnnotationsResolved({ currentUser, item }: ItemAndCurrentUser) {
    if (item && currentUser.hasTarlAccount) {
      return this.store.select(
        areUserAnnotationsLoadedForList(
          currentUser.oauthUser.guid,
          this.listId,
        ),
      );
    }
    return observableOf(item ? true : false);
  }

  private subscribedToResource({
    currentUser,
    resource,
  }: ResourceAndCurrentUser) {
    if (
      resource &&
      resource.isNonPageResource() &&
      currentUser.hasTarlAccount
    ) {
      return this.store.select(selectResourceSubscriptionStatus(resource.id));
    }
    return observableOf(LoadableFactory.loaded(false));
  }

  private readingIntention({ currentUser, resource }: ResourceAndCurrentUser) {
    if (
      resource &&
      resource.isNonPageResource() &&
      currentUser.hasTarlAccount
    ) {
      return this.store.select(
        selectReadingIntentionForUserAndResource(
          currentUser.oauthUser.guid,
          resource.id,
        ),
      );
    }
    return observableOf(null);
  }

  private studyNote({ currentUser, resource }: ResourceAndCurrentUser) {
    if (
      resource &&
      resource.isNonPageResource() &&
      currentUser.hasTarlAccount
    ) {
      return this.store.select(
        selectStudyNoteForUserAndResource(
          currentUser.oauthUser.guid,
          resource.id,
        ),
      );
    }
    return observableOf(null);
  }

  private pageContent(resource: BibliographicResource) {
    if (resource && resource.isPageResource()) {
      return this.store.select(selectPageContentForId(resource.id));
    }
    return observableOf(null);
  }

  onShowReportBrokenLinkModal($event: ReportBrokenLinkShowModalAction) {
    this.store.dispatch($event);
  }
}
